import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FiShoppingCart, FiHeart, FiArrowLeft, FiPlus, FiMinus } from "react-icons/fi";
import { MyDomain } from "../App";
import { useAppContext } from "../AppContext";

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "10px 0",
        borderBottom: "1px solid rgba(0,0,0,0.06)",
      }}
    >
      <span style={{ color: "#888", fontSize: 13, fontWeight: 500 }}>
        {label}
      </span>
      <span style={{ color: "#1a1a1a", fontSize: 13, fontWeight: 600 }}>
        {value}
      </span>
    </div>
  );
}

export default function ProductPage() {
  const { id } = useParams();
  const productId = parseInt(id);
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loginWarning, setLoginWarning] = useState(false);

  const { User, favIds, setFavIds, setFavCount, cartItems, setCartItems } = useAppContext();
  const favorited = favIds.has(productId);
  const cartAmount = cartItems[productId] || 0;
  const inCart = cartAmount > 0;

  const showLoginWarning = () => {
    setLoginWarning(true);
    setTimeout(() => setLoginWarning(false), 2500);
  };

  const handleFavoriteClick = async () => {
    if (!User) { showLoginWarning(); return; }
    try {
      if (!favorited) {
        await fetch(`${MyDomain}Products2/AddToFavorites`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId: User.id, productId }),
        });
        setFavIds((prev) => new Set([...prev, productId]));
        setFavCount((c) => c + 1);
      } else {
        await fetch(`${MyDomain}Products2/DeleteFromFavorites`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId: User.id, productId }),
        });
        setFavIds((prev) => { const n = new Set(prev); n.delete(productId); return n; });
        setFavCount((c) => Math.max(0, c - 1));
      }
    } catch (err) { console.error("Favorite action failed", err); }
  };

  const handleAddToCart = async () => {
    if (!User) { showLoginWarning(); return; }
    try {
      await fetch(`${MyDomain}Products2/AddToCart`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: User.id, productId }),
      });
      setCartItems((prev) => ({ ...prev, [productId]: (prev[productId] || 0) + 1 }));
    } catch (err) { console.error("Add to cart failed", err); }
  };

  const handleUpdateCart = async (newAmt) => {
    if (!User) return;
    try {
      if (newAmt <= 0) {
        await fetch(`${MyDomain}Products2/RemoveFromCart`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId: User.id, productId }),
        });
        setCartItems((prev) => { const n = { ...prev }; delete n[productId]; return n; });
      } else {
        await fetch(`${MyDomain}Products2/UpdateCartAmount`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId: User.id, productId, amount: newAmt }),
        });
        setCartItems((prev) => ({ ...prev, [productId]: newAmt }));
      }
    } catch (err) { console.error("Cart update failed", err); }
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(`${MyDomain}Products2/GetProduct/${id}`)
      .then((res) => {
        if (!res.ok) throw new Error("Product not found");
        return res.json();
      })
      .then((data) => {
        setProduct(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [id]);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            border: "3px solid #f3f3f3",
            borderTop: "3px solid #e11d48",
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 400,
          gap: 16,
        }}
      >
        <p style={{ color: "#999", fontSize: 16 }}>
          {error || "Product not found"}
        </p>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: "#e11d48",
            color: "white",
            border: "none",
            borderRadius: 10,
            padding: "10px 24px",
            cursor: "pointer",
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          Go Back
        </button>
      </div>
    );
  }

  const hasDiscount = product.priceAfterDiscount && product.priceAfterDiscount < product.price;
  const displayPrice = hasDiscount ? product.priceAfterDiscount : product.price;
  const discountPercent = hasDiscount
    ? Math.round(((product.price - product.priceAfterDiscount) / product.price) * 100)
    : 0;
  const inStock = product.inStock?.toLowerCase() !== "false" && product.inStock?.toLowerCase() !== "no" && product.inStock != null;

  return (
    <div style={{ padding: "24px 0 48px" }}>
      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "#666",
          fontSize: 14,
          fontWeight: 500,
          marginBottom: 24,
          padding: 0,
        }}
      >
        <FiArrowLeft size={16} />
        Back
      </button>

      {/* Main card */}
      <div
        style={{
          background: "#fff",
          borderRadius: 18,
          border: "1px solid rgba(0,0,0,0.08)",
          boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
          overflow: "hidden",
          display: "flex",
          flexWrap: "wrap",
        }}
      >
        {/* ── Image side ── */}
        <div
          style={{
            flex: "1 1 320px",
            minWidth: 280,
            position: "relative",
            background: "#f9f9f9",
          }}
        >
          <img
            src={product.image}
            alt={product.name}
            onError={(e) => {
              e.target.src =
                "https://placehold.co/600x600/f5f5f5/ccc?text=No+Image";
            }}
            style={{
              width: "100%",
              aspectRatio: "1 / 1",
              objectFit: "contain",
              display: "block",
            }}
          />

          {/* Discount badge */}
          {hasDiscount && discountPercent > 0 && (
            <span
              style={{
                position: "absolute",
                top: 16,
                left: 16,
                background: "#e11d48",
                color: "white",
                fontSize: 12,
                fontWeight: 700,
                padding: "4px 12px",
                borderRadius: 20,
              }}
            >
              -{discountPercent}%
            </span>
          )}

          {/* Deal badge */}
          {product.deal && (
            <span
              style={{
                position: "absolute",
                top: (hasDiscount && discountPercent > 0) ? 52 : 16,
                left: 16,
                background: "#7c3aed",
                color: "#fff",
                fontSize: 11,
                fontWeight: 700,
                padding: "4px 12px",
                borderRadius: 20,
              }}
            >
              {product.deal}
            </span>
          )}
        </div>

        {/* ── Info side ── */}
        <div
          style={{
            flex: "1 1 320px",
            minWidth: 280,
            padding: "32px 32px 36px",
            display: "flex",
            flexDirection: "column",
            gap: 18,
          }}
        >
          {/* Name */}
          <h1
            style={{
              margin: 0,
              fontSize: 22,
              fontWeight: 700,
              color: "#1a1a1a",
              lineHeight: 1.4,
            }}
          >
            {product.name}
          </h1>

          {/* Brand label */}
          {product.brand && (
            <p style={{ margin: 0, fontSize: 14, color: "#888" }}>
              Brand:{" "}
              <span style={{ color: "#1a1a1a", fontWeight: 600 }}>
                {product.brand}
              </span>
            </p>
          )}

          {/* Chips: category, gender, productType */}
          {(product.category || product.gender || product.productType) && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {product.category && (
                <span style={chipStyle("#f3f4f6", "#444")}>{product.category}</span>
              )}
              {product.gender && (
                <span style={chipStyle("#ede9fe", "#6d28d9")}>{product.gender}</span>
              )}
              {product.productType && (
                <span style={chipStyle("#fef3c7", "#92400e")}>{product.productType}</span>
              )}
            </div>
          )}

          {/* Price */}
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 30, fontWeight: 800, color: "#1a1a1a" }}>
              ₪{displayPrice}
            </span>
            {hasDiscount && (
              <span
                style={{
                  fontSize: 18,
                  color: "#bbb",
                  textDecoration: "line-through",
                }}
              >
                ₪{product.price}
              </span>
            )}
            {hasDiscount && discountPercent > 0 && (
              <span
                style={{
                  background: "#fef2f2",
                  color: "#e11d48",
                  fontSize: 13,
                  fontWeight: 700,
                  padding: "3px 10px",
                  borderRadius: 20,
                  border: "1px solid #fecdd3",
                }}
              >
                -{discountPercent}%
              </span>
            )}
          </div>

          {/* In Stock */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: inStock ? "#22c55e" : "#ef4444",
                display: "inline-block",
              }}
            />
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: inStock ? "#16a34a" : "#dc2626",
              }}
            >
              {inStock ? "In Stock" : "Out of Stock"}
            </span>
          </div>

          {/* Details table */}
          <div style={{ borderTop: "1px solid rgba(0,0,0,0.07)" }}>
            <InfoRow label="Volume" value={product.volume} />
            <InfoRow label="Deal" value={product.deal} />
            <InfoRow label="Gender" value={product.gender} />
          </div>

          {/* Description */}
          {product.description && (
            <div
              style={{
                background: "#f9fafb",
                borderRadius: 10,
                padding: "14px 16px",
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: 14,
                  color: "#555",
                  lineHeight: 1.8,
                }}
              >
                {product.description}
              </p>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: "flex", gap: 12, marginTop: 4, position: "relative" }}>
            {/* Cart button / qty controls */}
            {inCart ? (
              <div
                style={{
                  flex: 1, background: "#e11d48", borderRadius: 12, padding: "12px 16px",
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                }}
              >
                <button
                  onClick={() => handleUpdateCart(cartAmount - 1)}
                  style={{ background: "rgba(255,255,255,0.2)", border: "none", borderRadius: "50%", width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff" }}
                >
                  <FiMinus size={16} />
                </button>
                <span style={{ color: "#fff", fontSize: 16, fontWeight: 700 }}>{cartAmount}</span>
                <button
                  onClick={() => handleUpdateCart(cartAmount + 1)}
                  style={{ background: "rgba(255,255,255,0.2)", border: "none", borderRadius: "50%", width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff" }}
                >
                  <FiPlus size={16} />
                </button>
              </div>
            ) : (
              <button
                onClick={handleAddToCart}
                style={{
                  flex: 1,
                  background: inStock ? "#e11d48" : "#d1d5db",
                  color: "white", border: "none", borderRadius: 12,
                  padding: "14px 0", fontSize: 15, fontWeight: 700,
                  cursor: inStock ? "pointer" : "not-allowed",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                }}
                disabled={!inStock}
              >
                <FiShoppingCart size={18} />
                {inStock ? "Add to Cart" : "Out of Stock"}
              </button>
            )}

            {/* Favorite button */}
            <button
              onClick={handleFavoriteClick}
              style={{
                width: 52, height: 52, flexShrink: 0,
                background: favorited ? "rgba(225,29,72,0.08)" : "rgba(0,0,0,0.04)",
                border: `1.5px solid ${favorited ? "rgba(225,29,72,0.4)" : "rgba(0,0,0,0.12)"}`,
                borderRadius: 12, display: "flex", alignItems: "center",
                justifyContent: "center", cursor: "pointer",
                color: favorited ? "#e11d48" : "#999", transition: "all 0.2s",
              }}
            >
              <FiHeart size={20} fill={favorited ? "#e11d48" : "none"} stroke={favorited ? "#e11d48" : "currentColor"} />
            </button>

            {/* Login warning tooltip */}
            {loginWarning && (
              <div
                style={{
                  position: "absolute", bottom: "calc(100% + 10px)", left: 0,
                  background: "#1a1a1a", color: "#fff", fontSize: 13, fontWeight: 500,
                  padding: "8px 14px", borderRadius: 8, whiteSpace: "nowrap",
                  pointerEvents: "none", zIndex: 10,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                }}
              >
                Please login first
                <div style={{ position: "absolute", bottom: -5, left: 18, width: 10, height: 10, background: "#1a1a1a", transform: "rotate(45deg)" }} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function chipStyle(bg, color) {
  return {
    background: bg,
    color,
    fontSize: 12,
    fontWeight: 600,
    padding: "4px 12px",
    borderRadius: 20,
  };
}
